import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DevicesIcon from '@mui/icons-material/Devices';
import PublicIcon from '@mui/icons-material/Public';
import ChatIcon from '@mui/icons-material/Chat';
import SchoolIcon from '@mui/icons-material/School';
import CreateIcon from '@mui/icons-material/Create';
import GitHubIcon from '@mui/icons-material/GitHub';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';

/**
 * ProjectTypeSelectionDialog - Multi-step dialog for creating different project types
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {function} props.onCreateProject - Function called when creating a project
 */
const ProjectTypeSelectionDialog = ({
  open,
  onClose,
  onCreateProject
}) => {
  const [currentStep, setCurrentStep] = useState('main'); // 'main', 'application-options'
  const [selectedType, setSelectedType] = useState(null);

  // Reset to main step when dialog opens
  React.useEffect(() => {
    if (open) {
      setCurrentStep('main');
      setSelectedType(null);
    }
  }, [open]);

  // Main project types
  const projectTypes = [
    {
      id: 'application',
      title: 'Application',
      icon: <DevicesIcon />,
      description: '',
      hasSubOptions: true
    },
    {
      id: 'website',
      title: 'Website',
      icon: <PublicIcon />,
      description: '',
      hasSubOptions: false
    },
    {
      id: 'code-review',
      title: 'Code Review',
      icon: <CodeIcon />,
      description: '',
      hasSubOptions: false
    },
    {
      id: 'wcag',
      title: 'WCAG',
      icon: <AccessibilityNewIcon />,
      description: '',
      hasSubOptions: false
    }
  ];

  // Application sub-options
  const applicationOptions = [
    {
      id: 'scratch',
      title: 'Start from scratch',
      icon: <CreateIcon />,
      description: '',
      workflowType: 'scratch'
    },
    {
      id: 'existing',
      title: 'Start from existing code base',
      icon: <GitHubIcon />,
      description: '',
      workflowType: 'existing'
    },
    {
      id: 'ms',
      title: 'Maintenance & support',
      icon: <BusinessIcon />,
      description: '',
      workflowType: 'ms'
    }
  ];

  const handleMainSelection = (type) => {
    if (type.hasSubOptions) {
      setSelectedType(type);
      setCurrentStep('application-options');
    } else {
      // Direct navigation for types without sub-options
      onCreateProject({
        projectType: type.id,
        workflowType: null
      });
    }
  };

  const handleApplicationSelection = (option) => {
    onCreateProject({
      projectType: 'application',
      workflowType: option.workflowType
    });
  };

  const handleBack = () => {
    setCurrentStep('main');
    setSelectedType(null);
  };

  const renderMainStep = () => (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          pb: '14px',
          pt: '14px',
          pl: 5,
          pr: '10px',
          background: 'white'
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ color: '#212529', fontSize: '1.1rem' }}>
          Create new project
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 4, py: 5, px: 5, bgcolor: 'white' }}>
        <Box sx={{ px: 0, py: 0, bgcolor: '#fff' }}>
          <Grid container spacing={5}>
            {projectTypes.map((type) => (
            <Grid item xs={12} sm={6} md={3} key={type.id}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  bgcolor: '#f5f6f7',
                  color: '#343a40',
                  borderRadius: '4px',
                  height: '160px',
                  width: '100%',
                  maxWidth: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#ebeced'
                  }
                }}
                onClick={() => handleMainSelection(type)}
              >
                <Box 
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#4d5666',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    '& .MuiSvgIcon-root': {
                      fontSize: '2rem'
                    }
                  }}
                >
                  {type.icon}
                </Box>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  color="#343a40"
                  sx={{ fontSize: '1rem', mt: 1 }}
                >
                  {type.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                  }}
                >
                  {type.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        </Box>
      </DialogContent>
    </>
  );

  const renderApplicationOptionsStep = () => (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          pb: '14px',
          pt: '14px',
          pl: 5,
          pr: '10px',
          background: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              color: '#9e9e9e',
              mr: 1,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#666'
              }
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#212529', fontSize: '1.1rem' }}>
            Application type
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 4, py: 5, px: 5, bgcolor: 'white' }}>
        <Box sx={{ px: 0, py: 0, bgcolor: '#fff' }}>
          <Grid container spacing={5}>
          {applicationOptions.map((option) => (
            <Grid item xs={12} sm={4} md={3} key={option.id}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                  bgcolor: '#f5f6f7',
                  color: '#343a40',
                  borderRadius: '4px',
                  height: '160px',
                  width: '100%',
                  maxWidth: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#ebeced'
                  }
                }}
                onClick={() => handleApplicationSelection(option)}
              >
                <Box 
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#4d5666',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    '& .MuiSvgIcon-root': {
                      fontSize: '2rem'
                    }
                  }}
                >
                  {option.icon}
                </Box>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  color="#343a40"
                  sx={{ fontSize: '1rem', mt: 1 }}
                >
                  {option.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                  }}
                >
                  {option.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        </Box>
      </DialogContent>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 1,
          width: '90%',
          height: '90%',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'hidden',
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {currentStep === 'main' && renderMainStep()}
      {currentStep === 'application-options' && renderApplicationOptionsStep()}
    </Dialog>
  );
};

export default ProjectTypeSelectionDialog;